package com.swetdot.swetgram.data

import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.ChatKind
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.MessageKind
import com.swetdot.swetgram.domain.MessagePreview
import com.swetdot.swetgram.domain.MessageReaction
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.domain.UserId
import kotlin.time.Instant
import com.swetdot.swetgram.protocol.dto.Conversation as ConversationDto
import com.swetdot.swetgram.protocol.dto.ConversationType as ConversationTypeDto
import com.swetdot.swetgram.protocol.dto.Message as MessageDto
import com.swetdot.swetgram.protocol.dto.MessageReaction as MessageReactionDto
import com.swetdot.swetgram.protocol.dto.MessageSender as MessageSenderDto
import com.swetdot.swetgram.protocol.dto.MessageStatus as MessageStatusDto
import com.swetdot.swetgram.protocol.dto.MessageType as MessageTypeDto
import com.swetdot.swetgram.protocol.dto.UserPublic as UserPublicDto

/**
 * The single boundary between the wire contract and the client's own models. Nothing above this
 * file may import from `com.swetdot.swetgram.protocol` -- that is what keeps a contract change
 * from rippling into every screen (IMPLEMENTATION_NOTES §10).
 *
 * Parsing is deliberately forgiving. A malformed timestamp from a future server version should
 * degrade one row, not crash a screen the user is looking at.
 */

private fun parseInstant(raw: String?): Instant? =
    raw?.let { runCatching { Instant.parse(it) }.getOrNull() }

fun UserPublicDto.toDomain(): User = User(
    id = UserId(id),
    username = username,
    displayName = displayName,
    avatarUrl = avatarUrl,
    bio = bio,
    dateOfBirth = dateOfBirth,
    showBirthYear = showBirthYear,
    location = location,
    isOnline = isOnline ?: false,
    lastSeenAt = parseInstant(lastSeenAt),
    isDeleted = false,
    hasBlackBadge = hasBlackBadge,
)

fun MessageSenderDto.toDomain(): User = when (this) {
    is MessageSenderDto.Known -> user.toDomain()
    // A deleted account keeps its messages visible under "Deleted Account" -- the contract
    // allows its id to be null, so fall back to an empty id rather than inventing one.
    is MessageSenderDto.Deleted -> User(
        id = UserId(user.id.orEmpty()),
        username = "",
        displayName = user.displayName,
        avatarUrl = null,
        bio = null,
        isOnline = false,
        lastSeenAt = null,
        isDeleted = true,
    )
}

private fun MessageTypeDto.toDomain(): MessageKind = when (this) {
    MessageTypeDto.TEXT -> MessageKind.TEXT
    MessageTypeDto.IMAGE -> MessageKind.IMAGE
    MessageTypeDto.VOICE -> MessageKind.VOICE
    MessageTypeDto.SYSTEM -> MessageKind.SYSTEM
    MessageTypeDto.DISCOVER_POST -> MessageKind.SHARED_POST
    MessageTypeDto.PICSO_SHARE -> MessageKind.SHARED_POST
    MessageTypeDto.UNKNOWN -> MessageKind.UNKNOWN
}

/**
 * The server only ever reports `sent` or `read` -- `sending` and `failed` are client-local.
 * An unrecognised status must not be treated as more advanced than SENT, or a future state
 * would make the ticks lie.
 */
private fun MessageStatusDto.toDomain(): MessageState = when (this) {
    MessageStatusDto.SENT -> MessageState.SENT
    MessageStatusDto.READ -> MessageState.READ
    MessageStatusDto.UNKNOWN -> MessageState.SENT
}

fun MessageDto.toDomain(): Message = Message(
    id = MessageId(id),
    clientId = clientId,
    chatId = ChatId(conversationId),
    sender = sender.toDomain(),
    kind = type.toDomain(),
    text = sharedPost?.let { post ->
        listOfNotNull(
            content?.takeIf { it.isNotBlank() },
            post.authorDisplayName,
            post.contentPreview,
            post.url,
        ).joinToString("\n")
    } ?: content,
    imageUrl = media?.url?.takeIf { type == MessageTypeDto.IMAGE },
    voiceUrl = media?.url?.takeIf { type == MessageTypeDto.VOICE },
    voiceDurationMs = media?.durationMs,
    voiceWaveform = media?.waveform.orEmpty(),
    replyTo = replyToMessage?.let {
        MessagePreview(
            MessageId(it.id), UserId(it.senderId), it.senderDisplayName, it.preview,
            kind = it.type.toDomain(),
        )
    },
    state = status.toDomain(),
    // created_at is required by the contract; if it is unparseable the row is unusable for
    // ordering, so fall back to the epoch rather than dropping the message entirely.
    sentAt = parseInstant(createdAt) ?: Instant.fromEpochMilliseconds(0),
    editedAt = parseInstant(editedAt),
    isDeleted = false,
    version = version,
    canEdit = canEdit,
    canDeleteForEveryone = canDeleteForEveryone,
    reactions = reactions.map { it.toDomain() },
    picso = picso?.let { com.swetdot.swetgram.domain.PicsoMessageReference(it.id, it.expiresAt) },
)

fun MessageReactionDto.toDomain(): MessageReaction = MessageReaction(
    emoji = emoji,
    count = count,
    reactedByMe = reactedByMe,
)

fun ConversationDto.toDomain(): Chat = Chat(
    id = ChatId(id),
    kind = when (type) {
        ConversationTypeDto.GROUP -> ChatKind.GROUP
        else -> ChatKind.DIRECT
    },
    title = title,
    avatarUrl = avatarUrl,
    members = members.map { it.toDomain() },
    lastMessage = lastMessage?.toDomain(),
    unreadCount = unreadCount,
    isMuted = isMuted,
    isArchived = isArchived,
    isPinned = isPinned,
    updatedAt = parseInstant(updatedAt) ?: Instant.fromEpochMilliseconds(0),
)
